import { useState } from 'react'

import { Skeleton } from '@factorialco/deprecated-design-system'
import { F0Box } from '@factorialco/f0-react'
import { ProfileWorkDetailsTeamsDocument } from 'generated/resources/graphql'
import { useIntersectionObserver } from 'usehooks-ts'

import { useQuery } from 'modules/core/hooks/useQuery'
import Teams from 'modules/profile/components/Teams'

interface Props {
  employeeId: string
  isCurrentEmployee: boolean
  isEmployeeActive: boolean
}

const TeamsSectionSkeleton = () => {
  return (
    <F0Box
      display="flex"
      flexDirection="row"
      gap="lg"
      paddingY="xl"
      paddingX="4xl"
      justifyContent="between"
    >
      <F0Box display="flex" flexDirection="column" gap="sm">
        <Skeleton width="s32" height="s32" />
        <Skeleton width="s200" height="s16" />
        <Skeleton width="s360" height="s12" />
      </F0Box>
      <F0Box display="flex" flexDirection="column" gap="sm" width="96">
        <Skeleton width="full" height="s40" />
        <Skeleton width="full" height="s40" />
      </F0Box>
    </F0Box>
  )
}

const TeamsSection = ({ employeeId, isCurrentEmployee, isEmployeeActive }: Props) => {
  const [shouldFetch, setShouldFetch] = useState(false)
  const { ref } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '300px',
    onChange(isIntersecting) {
      if (isIntersecting) {
        setShouldFetch(true)
      }
    },
  })

  const { data, loading } = useQuery(ProfileWorkDetailsTeamsDocument, {
    variables: { employeeId },
    skip: !shouldFetch,
  })

  const canSeeTeams = data?.teams?._policies?.companySeeTeams?.can ?? false
  const canManageTeams = data?.teams?._policies?.teamManage?.can ?? false
  const memberships =
    data?.employees?.employee?.memberships?.filter((membership) => membership) ?? []
  const companyTeams = data?.teams?.teams ?? []

  return (
    <div ref={ref} style={{ minHeight: shouldFetch ? undefined : 1 }}>
      {shouldFetch && loading && <TeamsSectionSkeleton />}
      {shouldFetch && !loading && canSeeTeams && (
        <Teams
          employeeId={employeeId}
          isCurrentEmployee={isCurrentEmployee}
          isEmployeeActive={isEmployeeActive}
          employeeMemberships={memberships}
          teams={companyTeams}
          canManageTeams={canManageTeams}
        />
      )}
    </div>
  )
}

export default TeamsSection
